import { useCallback, useRef, useState } from 'react';

// Shared "Get Directions" geolocation flow for every GoogleMapEmbed consumer
// (public Veterinary Stations, FMLD Facilities, Geospatial Overview,
// Infrastructure map). Deliberately never falls back to Google's own "My
// Location" string on denial/failure — cross-origin iframes generally can't
// get geolocation permission, so that would silently show a broken or blank
// map instead of an honest "directions aren't available" state.
//
// `enableHighAccuracy: false` + our own watchdog timer (not just the
// getCurrentPosition `timeout` option) because on Windows, when the OS-level
// location provider is off/slow, Chrome's own geolocation timeout is
// unreliable and the call can hang with neither callback ever firing —
// which otherwise looks like the button silently does nothing.
const WATCHDOG_MS = 12000;

export function useDirectionsOrigin() {
  const [origin, setOrigin] = useState(null);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState('');
  const watchdogRef = useRef(null);

  const clearWatchdog = () => {
    if (watchdogRef.current) {
      window.clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
  };

  const requestDirections = useCallback(() => {
    // eslint-disable-next-line no-console
    console.debug('[useDirectionsOrigin] requestDirections() called');

    if (!navigator.geolocation) {
      // eslint-disable-next-line no-console
      console.debug('[useDirectionsOrigin] navigator.geolocation is unavailable');
      setError("Your browser can't share your location, so directions aren't available — showing the location instead.");
      return;
    }

    // Browsers silently refuse geolocation on plain HTTP unless the host is
    // localhost/127.0.0.1 — `window.isSecureContext` is the standard check
    // (true for https:, localhost, and 127.0.0.1). Calling it anyway there
    // often just hangs instead of firing the error callback, which is why
    // this needs to be caught explicitly rather than left to the API itself.
    if (!window.isSecureContext) {
      // eslint-disable-next-line no-console
      console.debug('[useDirectionsOrigin] insecure context, isSecureContext =', window.isSecureContext);
      setError(
        "This page isn't loaded over a secure connection (HTTPS), so your browser blocks location access here — open it via https:// (or http://localhost during development) and try again."
      );
      return;
    }

    setLocating(true);
    setError('');

    clearWatchdog();
    watchdogRef.current = window.setTimeout(() => {
      // eslint-disable-next-line no-console
      console.debug('[useDirectionsOrigin] watchdog fired — neither callback responded within', WATCHDOG_MS, 'ms');
      watchdogRef.current = null;
      setLocating(false);
      setError(
        "Couldn't get your location — check that location services are turned on for your device and browser, then try again."
      );
    }, WATCHDOG_MS);

    // eslint-disable-next-line no-console
    console.debug('[useDirectionsOrigin] calling navigator.geolocation.getCurrentPosition...');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        // eslint-disable-next-line no-console
        console.debug('[useDirectionsOrigin] success:', position.coords.latitude, position.coords.longitude);
        clearWatchdog();
        setOrigin(`${position.coords.latitude},${position.coords.longitude}`);
        setLocating(false);
      },
      (geoErr) => {
        // eslint-disable-next-line no-console
        console.debug('[useDirectionsOrigin] error callback, code =', geoErr.code, 'message =', geoErr.message);
        clearWatchdog();
        setError(
          geoErr.code === geoErr.PERMISSION_DENIED
            ? // A PERMISSION_DENIED here can come from either layer, and users
              // rarely know which — Windows itself often blocks location before
              // the browser ever shows its own prompt, which looks identical to
              // having clicked "Block". Point at both so either fix works.
              "Location access is blocked. Check: (1) Windows Settings → Privacy & security → Location — turn it on and allow your browser under \"Let apps access your location\", and (2) your browser's own site permission — click the icon next to the address bar and set Location to Allow. Then reload this page and try again."
            : geoErr.code === geoErr.TIMEOUT
              ? "Location took too long to determine — check that Windows Location is turned on (Settings → Privacy & security → Location), then try again."
              : "Couldn't determine your location, so directions aren't available — showing the location instead."
        );
        setLocating(false);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    );
  }, []);

  const reset = useCallback(() => {
    clearWatchdog();
    setOrigin(null);
    setError('');
    setLocating(false);
  }, []);

  return { origin, locating, error, requestDirections, reset };
}
